import { useEffect } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyleKit } from '@tiptap/extension-text-style';

interface MessageEditorProps {
  disabled: boolean;
  resetVersion: number;
  onChange: (text: string, html: string) => void;
}

const fonts = [
  ['Arial', 'Sans Serif'], ['Georgia', 'Serif'], ['Verdana', 'Wide'],
  ['Tahoma', 'Tahoma'], ['Trebuchet MS', 'Trebuchet MS'],
  ['Comic Sans MS', 'Comic Sans MS'], ['Courier New', 'Courier New'],
];

export default function MessageEditor({ disabled, resetVersion, onChange }: MessageEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, code: false,
        blockquote: false, horizontalRule: false, strike: false, link: false }),
      TextStyleKit.configure({ backgroundColor: false, color: false, lineHeight: false }),
    ],
    editorProps: {
      attributes: {
        class: 'message-editor', role: 'textbox', 'aria-label': 'message',
        'aria-multiline': 'true', 'aria-required': 'true',
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getText(), editor.getHTML()),
  });

  const formatting = useEditorState({
    editor,
    selector: ({ editor }) => ({
      font: editor?.getAttributes('textStyle').fontFamily || 'Arial',
      size: editor?.getAttributes('textStyle').fontSize || '14px',
      bold: editor?.isActive('bold') || false,
      italic: editor?.isActive('italic') || false,
      underline: editor?.isActive('underline') || false,
      bulletList: editor?.isActive('bulletList') || false,
      orderedList: editor?.isActive('orderedList') || false,
    }),
  });

  useEffect(() => { editor?.setEditable(!disabled, false); }, [editor, disabled]);
  useEffect(() => { if (resetVersion > 0) editor?.commands.clearContent(false); }, [editor, resetVersion]);

  return (
    <div className="editor-shell">
      <div className="format-toolbar" role="group" aria-label="Text formatting">
        <select aria-label="Font" title="Font" value={formatting?.font || 'Arial'} disabled={disabled || !editor}
          onChange={event => editor?.chain().focus().setFontFamily(event.target.value).run()}>
          {fonts.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select aria-label="Text size" title="Text size" value={formatting?.size || '14px'} disabled={disabled || !editor}
          onChange={event => editor?.chain().focus().setFontSize(event.target.value).run()}>
          <option value="10px">Small</option><option value="14px">Normal</option>
          <option value="18px">Large</option><option value="32px">Huge</option>
        </select>
        <span className="toolbar-divider" aria-hidden="true" />
        <button type="button" className="format-button" title="Bold (⌘/Ctrl+B)" aria-label="Bold" aria-pressed={formatting?.bold || false}
          onMouseDown={event => event.preventDefault()} onClick={() => editor?.chain().focus().toggleBold().run()}><b>B</b></button>
        <button type="button" className="format-button" title="Italic (⌘/Ctrl+I)" aria-label="Italic" aria-pressed={formatting?.italic || false}
          onMouseDown={event => event.preventDefault()} onClick={() => editor?.chain().focus().toggleItalic().run()}><i>I</i></button>
        <button type="button" className="format-button" title="Underline (⌘/Ctrl+U)" aria-label="Underline" aria-pressed={formatting?.underline || false}
          onMouseDown={event => event.preventDefault()} onClick={() => editor?.chain().focus().toggleUnderline().run()}><u>U</u></button>
        <span className="toolbar-divider" aria-hidden="true" />
        <button type="button" className="format-button" title="Bulleted list" aria-label="Bulleted list" aria-pressed={formatting?.bulletList || false}
          onMouseDown={event => event.preventDefault()} onClick={() => editor?.chain().focus().toggleBulletList().run()}>•≡</button>
        <button type="button" className="format-button" title="Numbered list" aria-label="Numbered list" aria-pressed={formatting?.orderedList || false}
          onMouseDown={event => event.preventDefault()} onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1.≡</button>
        <button type="button" className="format-button clear-format" title="Remove formatting" aria-label="Remove formatting"
          onMouseDown={event => event.preventDefault()} onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}>Tₓ</button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
